import React, { useState, useEffect, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Pagination from "@material-ui/lab/Pagination";
import * as XLSX from "xlsx";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import { i18n } from "../../translate/i18n";
import PageBase from "../../components/PageBase";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";

import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Box,
} from "@material-ui/core";
import { UsersFilter } from "../../components/UsersFilter";
import { TagsFilter } from "../../components/TagsFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import { StatusFilter } from "../../components/StatusFilter";
import useDashboard from "../../hooks/useDashboard";

import QueueSelectCustom from "../../components/QueueSelectCustom";
import moment from "moment";
import { getCompanyToday, resolveCompanyTimezone } from "../../utils/companyTimezone";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";

import { blue, green } from "@material-ui/core/colors";
import {
  Close,
  Facebook,
  Forward,
  History,
  Instagram,
  SaveAlt,
  Visibility,
  WhatsApp,
} from "@material-ui/icons";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { Field } from "formik";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    background: theme.palette.fancyBackground,
    overflowX: "hidden !important", // Remove scroll horizontal na base
    width: "100%",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  formControl: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  mainPaper: {
    flex: 1,
    marginTop: 40,
    borderRadius: 20,
    border: "0px !important",
    marginBottom: 40,
    overflow: "hidden",
  },
  mainPaperTable: {
    flex: 1, // Ocupa todo o espaço disponível no tableSpacing, menos o espaço da paginação
    overflow: "hidden", // Remove scroll abaixo da tabela
    minHeight: 0, // Permite que flex funcione corretamente
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
    overflowX: "hidden !important", // Remove scroll horizontal
    "& > *": {
      overflowX: "hidden !important",
    },
  },
  tableWrapper: {
    overflow: "auto",
    overflowX: "hidden !important", // Remove scroll horizontal interno
    flex: 1,
    minHeight: "400px", // Altura mínima ajustada
    ...theme.scrollbarStylesSoftBig,
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.mode === "light" ? "#ccc" : "#555",
      borderRadius: "4px",
    },
    "& table": {
      verticalAlign: "top !important",
    },
    "& tbody": {
      verticalAlign: "top !important",
    },
    "& tr": {
      verticalAlign: "top !important",
    },
  },
  titleSpacing: {
    paddingLeft: "10px !important",
    marginBottom: "10px",
    display: "block",
    flexShrink: 0,
  },
  tableSpacing: {
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden !important", // Remove scroll horizontal no container
    overflowY: "hidden !important", // Remove scroll vertical no container
    width: "100%",
    marginTop: "10px", // Padding de 10px entre o menu e o card da tabela
    paddingLeft: "5px", // Padding esquerdo para criar espaço do menu esquerdo
    paddingRight: "5px", // Padding direito da tabela
    flex: 1, // Ocupa todo o espaço disponível
    minHeight: 0, // Permite que flex funcione corretamente
    boxSizing: "border-box", // Inclui padding no cálculo da largura
  },
  selectNoScroll: {
    overflow: "hidden !important",
    "& .MuiInputLabel-root": {
      whiteSpace: "nowrap",
      overflow: "visible !important",
      textOverflow: "clip",
      lineHeight: "1.75 !important",
      paddingTop: "0px !important",
      marginTop: "0px !important",
      height: "auto !important",
      maxHeight: "none !important",
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 14px) scale(1) !important",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -4px) scale(0.75) !important",
        paddingTop: "0px !important",
        marginTop: "0px !important",
        lineHeight: "1.75 !important",
        height: "auto !important",
        maxHeight: "none !important",
        overflow: "visible !important",
      },
    },
    "& .MuiFormLabel-root": {
      overflow: "visible !important",
      height: "auto !important",
      maxHeight: "none !important",
    },
    "& .MuiFormControl-root": {
      overflow: "visible !important",
      paddingTop: "4px !important",
    },
    "& .MuiSelect-select": {
      overflowY: "hidden !important",
      overflow: "hidden !important",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      "&::-webkit-scrollbar": {
        display: "none !important",
        width: "0px !important",
        height: "0px !important",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      overflowY: "hidden",
      overflow: "hidden",
    },
    "& .MuiSelect-root": {
      overflowY: "hidden !important",
      overflow: "hidden !important",
      "&::-webkit-scrollbar": {
        display: "none !important",
        width: "0px !important",
        height: "0px !important",
      },
    },
    "& .MuiInputBase-root": {
      overflowY: "hidden !important",
      overflow: "hidden !important",
      "&::-webkit-scrollbar": {
        display: "none !important",
        width: "0px !important",
        height: "0px !important",
      },
    },
    "& .MuiOutlinedInput-input": {
      overflowY: "hidden !important",
      overflow: "hidden !important",
      whiteSpace: "nowrap",
      "&::-webkit-scrollbar": {
        display: "none !important",
        width: "0px !important",
        height: "0px !important",
      },
    },
    "& .MuiOutlinedInput-root": {
      overflowY: "hidden !important",
      overflow: "hidden !important",
      "&::-webkit-scrollbar": {
        display: "none !important",
        width: "0px !important",
        height: "0px !important",
      },
    },
    "& *": {
      "&::-webkit-scrollbar": {
        display: "none !important",
        width: "0px !important",
        height: "0px !important",
      },
      scrollbarWidth: "none !important",
      msOverflowStyle: "none !important",
    },
  },
  mainPaperFilter: {
    flex: 1,
    overflow: "auto",
    overflowX: "hidden", // Remove scroll horizontal
    // Removido height fixo para permitir ajuste automático baseado no conteúdo
    paddingBottom: "10px", // Padding bottom no card
    ...theme.scrollbarStylesSoftBig,
  },
  titleContainer: {
    marginTop: "10px", // Espaço antes do título
    paddingTop: "10px", // Garantir espaço visível
    display: "block",
  },
  filterContainer: {
    paddingTop: "10px", // Espaço antes da primeira caixa
    paddingBottom: "0px", // Sem padding bottom - o espaço vem do mainPaperFilter
    paddingLeft: "5px", // Padding esquerdo para não colar nas bordas
    paddingRight: "5px", // Padding direito para não colar nas bordas
  },
  mainHeaderBlock: {
    [theme.breakpoints.down("md")]: {
      display: "flex",
      flexWrap: "wrap",
    },
  },
  filterItem: {
    width: 200,
    [theme.breakpoints.down("md")]: {
      width: "45%",
    },
  },
  tableHeader: {
    background: theme.palette?.gradient 
      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary?.main || theme.palette.primary.main} 100%)`
      : theme.palette.primary.main,
    color: theme.palette.primary.contrastText || '#fff',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '12px 16px',
    '& .MuiTableCell-head': {
      color: theme.palette.primary.contrastText || '#fff',
      fontWeight: 600,
      fontSize: '0.875rem',
      borderBottom: `1px solid ${theme.palette.primary.contrastText || '#fff'}40`,
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(0, 0, 0, 0.06)' 
        : 'rgba(255, 255, 255, 0.1)',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.mode === 'light' 
        ? 'rgba(0, 0, 0, 0.03)' 
        : 'rgba(255, 255, 255, 0.04)',
    },
    '&:nth-of-type(odd)': {
      backgroundColor: 'transparent',
    },
  },
  tableCell: {
    padding: '6px 16px', // Reduzido de 12px para 6px para diminuir espaço entre linhas
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    verticalAlign: 'top !important',
  },
}));

const Reports = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const history = useHistory();
  const { getReport } = useDashboard();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Defina o tamanho da página

  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  // const [tagIds, setTagIds] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [options, setOptions] = useState([]);
  const companyTimezone = resolveCompanyTimezone(user?.company?.timezone || localStorage.getItem("companyTimezone"));
  const [dateFrom, setDateFrom] = useState(
    moment().tz(companyTimezone).startOf("month").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(getCompanyToday(companyTimezone));
  const [onlyRated, setOnlyRated] = useState(false);
  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);

  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [openTicketDetailsModal, setOpenTicketDetailsModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Atualizar tabela quando pageSize mudar
  useEffect(() => {
    if (tickets.length > 0 || pageNumber > 1) {
      handleFilter(1); // Resetar para página 1 quando mudar o tamanho
    }
  }, [pageSize]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  // const handleSelectedTags = (selecteds) => {
  //   const tags = selecteds.map((t) => t.id);
  //   setTagIds(tags);
  // };

  const exportarGridParaExcel = async () => {
    setLoading(true); // Define o estado de loading como true durante o carregamento

    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        // tags: JSON.stringify(tagIds),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        // tags: tagIds,
        dateFrom,
        dateTo,
        page: 1, // Passa o número da página para a API
        pageSize: 9999999, // Passa o tamanho da página para a API
        onlyRated: onlyRated ? "true" : "false",
      });

      const ticketsData = data.tickets.map((ticket) => {
        const formatDate = (dateString) => {
          if (!dateString || dateString === null) {
            return { date: "", time: "" };
          }

          if (dateString.includes("/") && dateString.includes(" ")) {
            const [datePart, timePart] = dateString.split(" ");
            return {
              date: datePart,
              time: timePart,
            };
          }

          const date = new Date(dateString);
          if (isNaN(date.getTime())) {
            return { date: "", time: "" };
          }

          return {
            date: date.toLocaleDateString("pt-BR"),
            time: date.toLocaleTimeString("pt-BR"),
          };
        };

        const formatSupportTime = (timeString) => {
          if (!timeString) return "";

          if (timeString === "0") return "0min";

          const match = timeString.match(
            /(\d+)\s*d,?\s*(\d+)\s*hr?s?\s*e?\s*(\d+)\s*m/
          );
          if (!match) return timeString;

          const days = parseInt(match[1]);
          const hours = parseInt(match[2]);
          const minutes = parseInt(match[3]);

          let result = "";
          if (days > 0) result += `${days}d`;
          if (hours > 0) result += `${hours}h`;
          if (minutes > 0) result += `${minutes}min`;

          return result || "0min";
        };

        const createdAtFormatted = formatDate(ticket.createdAt);
        const closedAtFormatted = formatDate(ticket.closedAt);

        return {
          id: ticket.id,
          Conexão: ticket.whatsappName,
          Contato: ticket.contactName,
          Usuário: ticket.userName,
          Fila: ticket.queueName,
          Status: ticket.status,
          ÚltimaMensagem: ticket.lastMessage,
          DataAbertura: createdAtFormatted.date,
          HoraAbertura: createdAtFormatted.time,
          DataFechamento: closedAtFormatted.date,
          HoraFechamento: closedAtFormatted.time,
          TempoDeAtendimento: formatSupportTime(ticket.supportTime),
          nps: ticket.NPS || "",
          ValorDaVenda:
            ticket.valorVenda !== undefined && ticket.valorVenda !== null
              ? ticket.valorVenda
              : "-",
          MotivoDaNaoVenda: ticket.motivoNaoVenda || "-",
          FinalizadoComVenda:
            ticket.finalizadoComVenda === true
              ? "Sim"
              : ticket.finalizadoComVenda === false
              ? "Não"
              : "-",
        };
      });

      console.log(ticketsData);

      // RDS.CHAT - preparando os dados aqui...
      const getTranslation = (key, fallback) => {
        const translation = i18n.t(key);
        return translation !== key ? translation : fallback;
      };

      const formatDateBR = (dateStr) => {
        if (!dateStr)
          return getTranslation(
            "reports.exportExcel.notInformed",
            "Não informado"
          );
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
      };

      const translations = {
        title: getTranslation(
          "reports.exportExcel.title",
          "Relatórios de Atendimentos"
        ),
        startDate: getTranslation(
          "reports.exportExcel.startDate",
          "Data inicial"
        ),
        endDate: getTranslation("reports.exportExcel.endDate", "Data final"),
        columns: {
          id: getTranslation("reports.exportExcel.columns.id", "ID"),
          connection: getTranslation(
            "reports.exportExcel.columns.connection",
            "Conexão"
          ),
          contact: getTranslation(
            "reports.exportExcel.columns.contact",
            "Contato"
          ),
          user: getTranslation("reports.exportExcel.columns.user", "Usuário"),
          queue: getTranslation("reports.exportExcel.columns.queue", "Fila"),
          status: getTranslation(
            "reports.exportExcel.columns.status",
            "Status"
          ),
          lastMessage: getTranslation(
            "reports.exportExcel.columns.lastMessage",
            "Última Mensagem"
          ),
          openDate: getTranslation(
            "reports.exportExcel.columns.openDate",
            "Data Abertura"
          ),
          openTime: getTranslation(
            "reports.exportExcel.columns.openTime",
            "Hora Abertura"
          ),
          closeDate: getTranslation(
            "reports.exportExcel.columns.closeDate",
            "Data Fechamento"
          ),
          closeTime: getTranslation(
            "reports.exportExcel.columns.closeTime",
            "Hora Fechamento"
          ),
          supportTime: getTranslation(
            "reports.exportExcel.columns.supportTime",
            "Tempo de Atendimento"
          ),
          nps: getTranslation("reports.exportExcel.columns.nps", "NPS"),
          valorVenda: getTranslation(
            "reports.exportExcel.columns.valorVenda",
            "Valor da Venda"
          ),
          motivoNaoVenda: getTranslation(
            "reports.exportExcel.columns.motivoNaoVenda",
            "Motivo da Não Venda"
          ),
          finalizadoComVenda: getTranslation(
            "reports.exportExcel.columns.finalizadoComVenda",
            "Finalizado com Venda"
          ),
        },
      };

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([
        [translations.title],
        [`${translations.startDate}: ${formatDateBR(dateFrom)}`],
        [`${translations.endDate}: ${formatDateBR(dateTo)}`],
        [],
        [
          translations.columns.id,
          translations.columns.connection,
          translations.columns.contact,
          translations.columns.user,
          translations.columns.queue,
          translations.columns.status,
          translations.columns.lastMessage,
          translations.columns.openDate,
          translations.columns.openTime,
          translations.columns.closeDate,
          translations.columns.closeTime,
          translations.columns.supportTime,
          translations.columns.nps,
          translations.columns.valorVenda,
          translations.columns.motivoNaoVenda,
          translations.columns.finalizadoComVenda,
        ],
      ]);

      XLSX.utils.sheet_add_json(ws, ticketsData, {
        origin: "A6",
        skipHeader: true,
      });

      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({ s: { c: 0, r: 0 }, e: { c: 12, r: 0 } });

      if (!ws["!cols"]) ws["!cols"] = [];
      ws["!cols"] = [
        { wch: 8 },
        { wch: 15 },
        { wch: 25 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 30 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 18 },
        { wch: 8 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];

      if (!ws["!rows"]) ws["!rows"] = [];
      ws["!rows"] = [
        { hpt: 25 },
        { hpt: 20 },
        { hpt: 20 },
        { hpt: 10 },
        { hpt: 25 },
      ];

      const range = XLSX.utils.decode_range(ws["!ref"]);

      for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
        for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
          if (!ws[cellAddress]) continue;

          if (!ws[cellAddress].s) ws[cellAddress].s = {};

          if (rowNum === 0) {
            ws[cellAddress].s = {
              font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "4472C4" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          } else if (rowNum === 1 || rowNum === 2) {
            ws[cellAddress].s = {
              font: { bold: true, sz: 12 },
              fill: { fgColor: { rgb: "E7E6E6" } },
            };
          } else if (rowNum === 4) {
            ws[cellAddress].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "70AD47" } },
              alignment: { horizontal: "center", vertical: "center" },
            };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "RelatorioDeAtendimentos");
      XLSX.writeFile(wb, "relatorio-de-atendimentos.xlsx", {
        cellStyles: true,
        bookType: "xlsx",
      });

      setPageNumber(pageNumber);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (pageNumber) => {
    setLoading(true); // Define o estado de loading como true durante o carregamento
    console.log(onlyRated);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        // tags: JSON.stringify(tagIds),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        // tags: tagIds,
        dateFrom,
        dateTo,
        page: pageNumber, // Passa o número da página para a API
        pageSize: pageSize, // Passa o tamanho da página para a API
        onlyRated: onlyRated ? "true" : "false",
      });

      setTotalTickets(data.totalTickets.total);

      // Verifica se há mais resultados para definir hasMore
      setHasMore(data.tickets.length === pageSize);

      setTickets(data.tickets); // Se for a primeira página, substitua os tickets

      setPageNumber(pageNumber); // Atualiza o estado da página atual
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false); // Define o estado de loading como false após o carregamento
    }
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setUserIds(users);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);
    setSelectedWhatsapp(whatsapp);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);

    setSelectedStatus(statusFilter);
  };
  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return (
          <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />
        );
      case "instagram":
        return (
          <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />
        );
      case "whatsapp":
        return (
          <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
        );
      default:
        return "error";
    }
  };

  const renderOption = (option) => {
    if (option.number) {
      return (
        <>
          {IconChannel(option.channel)}
          <Typography
            component="span"
            style={{
              fontSize: 14,
              marginLeft: "10px",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: "2",
            }}
          >
            {option.name} - {option.number}
          </Typography>
        </>
      );
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const handleSelectOption = (e, newValue) => {
    setSelectedContactId(newValue.id);
    setSearchParam("");
  };

  const renderOptionLabel = (option) => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };
  const filter = createFilterOptions({
    trim: true,
  });

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };
  const renderContactAutocomplete = () => {
    return (
      <Grid xs={12} item>
        <Autocomplete
          fullWidth
          options={options}
          loading={loading}
          clearOnBlur
          autoHighlight
          freeSolo
          size="small"
          clearOnEscape
          getOptionLabel={renderOptionLabel}
          renderOption={renderOption}
          filterOptions={createAddContactOption}
          onChange={(e, newValue) => handleSelectOption(e, newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("newTicketModal.fieldLabel")}
              variant="outlined"
              autoFocus
              size="small"
              onChange={(e) => setSearchParam(e.target.value)}
              // onKeyPress={(e, newValue) => handleSelectOption(e, newValue)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </Grid>
    );
  };

  const handleCloseTicketDetailsModal = () => {
    setOpenTicketDetailsModal(false);
    setSelectedTicket(null);
  };

  return (
    <PageBase
      className="reports-page app-page-root"
      title={i18n.t("reports.title")}
      toolbar={
        <div className={classes.filterContainer}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              {renderContactAutocomplete()}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatusFilter onFiltered={handleSelectedStatus} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <UsersFilter onFiltered={handleSelectedUsers} />
            </Grid>
            <Grid item xs={12} sm={6} md={3} style={{ display: "flex", alignItems: "flex-end" }}>
              <div style={{ width: "100%", marginTop: "0px", marginBottom: "0px" }}>
                <QueueSelectCustom
                  selectedQueueIds={queueIds}
                  onChange={(values) => setQueueIds(values)}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3} style={{ display: "flex", alignItems: "flex-end" }}>
              <TextField
                label={i18n.t("reports.startDate")}
                type="date"
                value={dateFrom}
                variant="outlined"
                fullWidth
                size="small"
                margin="dense"
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} style={{ display: "flex", alignItems: "flex-end" }}>
              <TextField
                label={i18n.t("reports.endDate")}
                type="date"
                value={dateTo}
                variant="outlined"
                fullWidth
                size="small"
                margin="dense"
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", gap: "8px" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={onlyRated}
                    onChange={() => setOnlyRated(!onlyRated)}
                  />
                }
                label={i18n.t("reports.buttons.onlyRated")}
              />
              <IconButton
                onClick={exportarGridParaExcel}
                aria-label="Exportar para Excel"
              >
                <SaveAlt />
              </IconButton>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleFilter(pageNumber)}
                size="small"
                style={{
                  height: "40px",
                  minWidth: "120px"
                }}
              >
                {i18n.t("reports.buttons.filter")}
              </Button>
            </Grid>
          </Grid>
        </div>
      }
      footer={
        <div style={{ marginTop: "8px", marginBottom: "0px", display: "flex", width: "100%", overflowX: "hidden", paddingLeft: "5px", paddingRight: "5px", flexShrink: 0, boxSizing: "border-box", alignItems: "center" }}>
          <Grid container spacing={2} style={{ width: "100%", margin: 0, boxSizing: "border-box", alignItems: "center" }}>
            <Grid item xs={12} sm={10} md={10} style={{ paddingLeft: "8px", paddingRight: "8px", boxSizing: "border-box", display: "flex", alignItems: "center" }}>
              <Pagination
                count={Math.ceil(totalTickets / pageSize) || 1}
                page={pageNumber}
                onChange={(event, value) => handleFilter(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Grid>
            <Grid item xs={12} sm={2} md={2} style={{ paddingLeft: "8px", paddingRight: "8px", boxSizing: "border-box", display: "flex", alignItems: "center" }}>
              <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectNoScroll}>
                <InputLabel
                  id="dialog-select-prompt-label"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "visible !important",
                    textOverflow: "clip",
                    lineHeight: "1.75",
                    paddingTop: "0px",
                    marginTop: "0px",
                    height: "auto",
                    maxHeight: "none",
                  }}
                >
                  {i18n.t("tickets.search.ticketsPerPage")}
                </InputLabel>
                <Select
                  labelId="dialog-select-prompt-label"
                  id="dialog-select-prompt"
                  name="pageSize"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                  }}
                  label={i18n.t("tickets.search.ticketsPerPage")}
                  fullWidth
                  style={{
                    overflowY: "hidden !important",
                    overflow: "hidden !important",
                  }}
                  inputProps={{
                    style: {
                      overflowY: "hidden !important",
                      overflow: "hidden !important",
                      whiteSpace: "nowrap",
                    },
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "center",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "center",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        maxHeight: "300px",
                        overflowY: "auto",
                      },
                    },
                  }}
                >
                  <MenuItem value={5}>{"5"}</MenuItem>
                  <MenuItem value={10}>{"10"}</MenuItem>
                  <MenuItem value={20}>{"20"}</MenuItem>
                  <MenuItem value={50}>{"50"}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </div>
      }
    >
      {openTicketMessageDialog && (
        <ShowTicketLogModal
          isOpen={openTicketMessageDialog}
          handleClose={() => setOpenTicketMessageDialog(false)}
          ticketId={ticketOpen.id}
        />
      )}
      
      {/* Modal de Detalhes Completos do Ticket */}
      <Dialog
        open={openTicketDetailsModal}
        onClose={handleCloseTicketDetailsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            margin: '32px auto',
            maxHeight: 'calc(100vh - 64px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          padding: '16px 24px',
          flexShrink: 0,
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ color: '#fff', fontWeight: 600 }}>
              Detalhes do Ticket #{selectedTicket?.id}
            </Typography>
            <IconButton
              onClick={handleCloseTicketDetailsModal}
              style={{ color: '#fff' }}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ 
          padding: '24px',
          overflowY: 'auto',
          flex: '1 1 auto',
        }}>
          {selectedTicket && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.whatsapp")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.whatsappName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.contact")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.contactName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.user")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.userName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.queue")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.queueName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("wallets.wallet")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.walletName || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.status")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.status || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.lastMessage")}
                </Typography>
                <Typography variant="body1" gutterBottom style={{ wordBreak: 'break-word' }}>
                  {selectedTicket?.lastMessage || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.dateOpen")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.createdAt || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.dateClose")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.closedAt || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.supportTime")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.supportTime || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {i18n.t("reports.table.NPS")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.NPS || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Valor da Venda
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.valorVenda !== undefined &&
                  selectedTicket?.valorVenda !== null
                    ? selectedTicket.valorVenda
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Motivo da Não Venda
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.motivoNaoVenda || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Finalizado com Venda
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedTicket?.finalizadoComVenda === true
                    ? "Sim"
                    : selectedTicket?.finalizadoComVenda === false
                    ? "Não"
                    : "-"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions style={{ 
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
          flexShrink: 0,
        }}>
          <Button
            onClick={() => {
              if (selectedTicket) {
                setOpenTicketMessageDialog(true);
                setTicketOpen(selectedTicket);
                handleCloseTicketDetailsModal();
              }
            }}
            color="primary"
            startIcon={<History />}
          >
            Ver Logs
          </Button>
          <Button
            onClick={() => {
              if (selectedTicket) {
                history.push(`/tickets/${selectedTicket.uuid}`);
              }
            }}
            color="primary"
            variant="contained"
            startIcon={<Forward />}
          >
            Acessar Ticket
          </Button>
          <Button onClick={handleCloseTicketDetailsModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <div className="page-table-wrapper app-table-zebra">
        <Paper className={classes.mainPaperTable} variant="outlined">
        <div className={classes.tableWrapper}>
        <Table size="small" id="grid-attendants" style={{ minHeight: "400px", width: "100%", tableLayout: "fixed", verticalAlign: "top" }}>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell align="center" className={classes.tableCell} style={{ width: "7%" }}>
                {i18n.t("reports.table.id")}
              </TableCell>
              <TableCell align="left" className={classes.tableCell} style={{ width: "18%" }}>
                {i18n.t("reports.table.contact")}
              </TableCell>
              <TableCell align="left" className={classes.tableCell} style={{ width: "13%" }}>
                Número do Cliente
              </TableCell>
              <TableCell align="center" className={classes.tableCell} style={{ width: "13%" }}>
                Abertura
              </TableCell>
              <TableCell align="center" className={classes.tableCell} style={{ width: "13%" }}>
                Fechamento
              </TableCell>
              <TableCell align="center" className={classes.tableCell} style={{ width: "10%" }}>
                {i18n.t("reports.table.status")}
              </TableCell>
              <TableCell align="center" className={classes.tableCell} style={{ width: "8%" }}>
                Avaliação
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {tickets.map((ticket, index) => (
                <TableRow 
                  key={ticket.id}
                  onClick={() => {
                    setSelectedTicket(ticket);
                    setOpenTicketDetailsModal(true);
                  }}
                  className={classes.tableRow}
                  style={{
                    backgroundColor: index % 2 === 1 
                      ? (theme.palette.type === 'light' 
                          ? 'rgba(0, 0, 0, 0.03)' 
                          : 'rgba(255, 255, 255, 0.04)')
                      : 'transparent'
                  }}
                >
                  <TableCell align="center" className={classes.tableCell}>
                    {ticket.id}
                  </TableCell>
                  <TableCell align="left" className={classes.tableCell}>
                    {ticket?.contactName || "-"}
                  </TableCell>
                  <TableCell align="left" className={classes.tableCell}>
                    {ticket?.contactNumber && ticket.contactNumber !== '-' ? ticket.contactNumber : "-"}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    {ticket?.createdAt || "-"}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    {ticket?.closedAt || "-"}
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    <Typography
                      variant="body2"
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        backgroundColor: ticket?.status === 'FECHADO' 
                          ? theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20'
                          : ticket?.status === 'ABERTO'
                          ? theme.palette.mode === 'light' ? '#fff3e0' : '#e65100'
                          : theme.palette.mode === 'light' ? '#f3e5f5' : '#4a148c',
                        color: ticket?.status === 'FECHADO'
                          ? theme.palette.mode === 'light' ? '#2e7d32' : '#81c784'
                          : ticket?.status === 'ABERTO'
                          ? theme.palette.mode === 'light' ? '#e65100' : '#ffb74d'
                          : theme.palette.mode === 'light' ? '#7b1fa2' : '#ba68c8',
                      }}
                    >
                      {ticket?.status}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    {ticket?.NPS && ticket.NPS > 0 ? (
                      <Typography
                        variant="body2"
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          backgroundColor: ticket.NPS >= 9
                            ? theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20'
                            : ticket.NPS >= 7
                            ? theme.palette.mode === 'light' ? '#fff3e0' : '#e65100'
                            : theme.palette.mode === 'light' ? '#ffebee' : '#b71c1c',
                          color: ticket.NPS >= 9
                            ? theme.palette.mode === 'light' ? '#2e7d32' : '#81c784'
                            : ticket.NPS >= 7
                            ? theme.palette.mode === 'light' ? '#e65100' : '#ffb74d'
                            : theme.palette.mode === 'light' ? '#c62828' : '#ef5350',
                        }}
                      >
                        {ticket.NPS}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#757575' : '#bdbdbd',
                        }}
                      >
                        Não Avaliado
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={7} />}
            </>
          </TableBody>
        </Table>
        </div>
      </Paper>
      
      </div>
    </PageBase>
  );
};

export default Reports;
